/**
 * Zentrale Sammlung **aller** sichtbaren Texte. Die Anwendung ist
 * ausschließlich deutsch; es gibt keine Übersetzungsschicht.
 *
 * Regeln:
 *  - Kein sichtbarer String steht in einer Komponente – immer hier.
 *  - Texte aus der Alt-Anwendung wurden übernommen, Tippfehler korrigiert
 *    ("zugehlörigen" → "zugehörigen", "diesen Patient" → "diesen Patienten").
 *  - Platzhalter in geschweiften Klammern werden über die `format`-Helfer
 *    (unten) ersetzt.
 */

export const de = {
  app: {
    name: 'SELWA',
    title: 'SELWA für Therapeuten',
    /** Suffix für den Dokumenttitel: "<Seite> – SELWA für Therapeuten". */
    titleSuffix: 'SELWA für Therapeuten',
    logoAlt: 'SELWA Logo',
  },

  nav: {
    patients: 'Patienten',
    newPatient: 'Neuer Patient',
    license: 'Lizenz',
    administration: 'Verwaltung',
    imprint: 'Impressum',
    signOut: 'Abmelden',
    openMenu: 'Menü öffnen',
    closeMenu: 'Menü schließen',
    menuTitle: 'Navigation',
    adminTherapists: 'Therapeuten',
    adminLicenses: 'Lizenzen',
  },

  actions: {
    save: 'Speichern',
    cancel: 'Abbrechen',
    delete: 'Löschen',
    remove: 'Entfernen',
    add: 'Hinzufügen',
    create: 'Anlegen',
    edit: 'Bearbeiten',
    close: 'Schließen',
    print: 'Drucken',
    download: 'Herunterladen',
    search: 'Suchen',
    retry: 'Erneut versuchen',
    back: 'Zurück',
    backToPatients: 'Zurück zur Patientenliste',
    reload: 'Neu laden',
  },

  auth: {
    signInTitle: 'Anmelden',
    signInSubtitle: 'Bitte melden Sie sich mit Ihren Zugangsdaten an.',
    username: 'Benutzername',
    password: 'Passwort',
    signIn: 'Anmelden',
    signingIn: 'Anmeldung läuft …',
    invalidCredentials: 'Benutzername oder Passwort falsch.',
    licenseExpired:
      'Keine Lizenz vorhanden oder Lizenz abgelaufen. Bitte kontaktieren Sie Susanne Thielen.',
    rateLimited: 'Zu viele Anmeldeversuche. Bitte warten Sie einen Moment.',
    usernameRequired: 'Bitte geben Sie Ihren Benutzernamen ein.',
    passwordRequired: 'Bitte geben Sie Ihr Passwort ein.',
    signedOut: 'Sie wurden abgemeldet.',
    sessionExpired: 'Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.',
    changePasswordTitle: 'Passwort ändern',
    currentPassword: 'Aktuelles Passwort',
    newPassword: 'Neues Passwort',
    newPasswordRepeat: 'Neues Passwort wiederholen',
    passwordMismatch: 'Die Passwörter stimmen nicht überein.',
    passwordChanged: 'Das Passwort wurde geändert.',
    mustChangePassword: 'Bitte vergeben Sie ein neues Passwort.',
  },

  patients: {
    listTitle: 'Patienten nach Nummer',
    searchPlaceholder: 'Suche…',
    searchLabel: 'Patienten durchsuchen',
    count: '{count} Patienten',
    countOne: '1 Patient',
    empty: 'Es sind noch keine Patienten angelegt.',
    emptyHint: 'Legen Sie über „Neuer Patient“ den ersten Patienten an.',
    emptySearch: 'Keine Patienten gefunden.',
    emptySearchHint: 'Bitte prüfen Sie Ihre Suchanfrage.',
    entryCount: '{count} Abfragen',
    entryCountOne: '1 Abfrage',
    entryCountNone: 'Noch keine Abfragen',
    newAction: 'Neuer Patient',
    openAction: 'Patient {name} öffnen',
    searchClear: 'Suche zurücksetzen',
    searchResults: '{count} von {total} Patienten',
    removeAction: 'Patient entfernen',
    removeActionFor: 'Patient {name} entfernen',
    deleteDialog: {
      title: 'Patient entfernen',
      question: 'Wollen Sie diesen Patienten wirklich entfernen?',
      warning: 'Alle zugehörigen Daten werden gelöscht.',
      cancel: 'Abbrechen',
      confirm: 'Löschen',
    },
    deleted: 'Der Patient wurde entfernt.',
    deleteFailed: 'Der Patient konnte nicht entfernt werden.',
  },

  newPatient: {
    title: 'Neuer Patient',
    /** Datenschutzhinweis über dem Formular. */
    intro:
      'Vergeben Sie eine pseudonyme Patienten-ID – bitte keine echten Namen, '
      + 'Geburtsdaten oder andere personenbezogenen Angaben.',
    patientId: 'Patienten-ID',
    patientIdHint: 'Pseudonym, 4–30 Zeichen, keine Leerzeichen.',
    patientIdPlaceholder: 'z. B. pat-0815',
    submit: 'Hinzufügen',
    submitting: 'Wird angelegt …',
    created: 'Der Patient wurde angelegt.',
    licenseLink: 'Zur Lizenzübersicht',
    errors: {
      required: 'Bitte geben Sie eine Patienten-ID ein.',
      pattern:
        'Erlaubt sind Buchstaben, Ziffern sowie . _ - + @ – keine Leerzeichen und keine Umlaute.',
      length: 'Die Patienten-ID muss zwischen 4 und 30 Zeichen lang sein.',
      exists: 'Diese Patienten-ID existiert bereits.',
      quota:
        'Sie haben die maximale Anzahl an Patienten für Ihre Lizenz erreicht. '
        + 'Bitte kontaktieren Sie Susanne Thielen, wenn Sie mehr Plätze benötigen.',
      licenseExpired:
        'Ihre Lizenz ist abgelaufen. Bitte kontaktieren Sie Susanne Thielen zur Verlängerung.',
      generic: 'Die Eingaben sind unvollständig oder ungültig.',
    },
  },

  patient: {
    title: 'Patient: {name}',
    heading: 'Patient',
    qrTitle: 'QR-Code zum Koppeln',
    qrAlt: 'QR-Code für Patient {name}',
    qrCaption: 'In der SELWA-App scannen, um das Gerät mit diesem Patienten zu koppeln.',
    actions: {
      print: 'Drucken',
      printInfoSheet: 'Informationsblatt für den Patienten drucken',
      exportExcel: 'Excel-Dokument downloaden',
      exporting: 'Export wird erstellt …',
      exportFailed: 'Der Export konnte nicht erstellt werden.',
      logSection: 'Aktionen für diesen Patienten',
    },
    table: {
      timeStarted: 'Startzeit',
      timeFinished: 'Endzeit',
      aspect: 'Aspekt',
      description: 'Beschreibung',
      matching: 'Passend',
      change: 'Veränderung',
      sstKnown: 'SST bekannt',
      sstApplied: 'SST angewendet',
      sstRating: 'SST Bewertung',
      sstRatingText: 'Anmerkung',
      phase: 'Phase',
    },
    aspects: {
      koerperreaktion: 'Körperreaktion',
      gefuehl: 'Gefühl',
      gedanken: 'Gedanken',
    },
    tristate: {
      yes: '✓',
      no: '✘',
      yesLabel: 'Ja',
      noLabel: 'Nein',
      notReached: 'In dieser Phase nicht erreicht',
    },
    rating: {
      alt: 'Bewertung {value} von 5',
      none: 'Keine Bewertung',
    },
    chart: {
      title: 'Abfragen pro Tag',
      show: 'Abfragen pro Tag anzeigen',
      hide: 'Abfragen pro Tag ausblenden',
      yAxis: 'Abfragen',
      xAxis: 'Datum',
      empty: 'Noch keine Abfragen vorhanden.',
      tooltip: '{date}: {count} Abfragen',
      tooltipOne: '{date}: 1 Abfrage',
      description: 'Balkendiagramm der Abfragen pro Tag, {days} Tage, höchstens {max} pro Tag.',
      /** Sehr langer Zeitraum: Tage ohne Abfragen werden nicht aufgefüllt. */
      gapsOmitted:
        'Sehr langer Zeitraum: Tage ohne Abfragen sind nicht dargestellt, '
        + 'die Abstände zwischen den Balken sind daher nicht maßstäblich.',
    },
    filter: {
      title: 'Filter',
      from: 'Von',
      to: 'Bis',
      apply: 'Anwenden',
      reset: 'Zurücksetzen',
      perPage: 'Einträge pro Seite',
      range: 'Zeitraum',
      rangePlaceholder: 'Zeitraum wählen',
      sort: 'Sortierung',
      sortNewest: 'Neueste zuerst',
      sortOldest: 'Älteste zuerst',
    },
    empty: 'Für diesen Patienten wurden noch keine Abfragen übertragen.',
    emptyHint: 'Sobald der Patient die App nutzt, erscheinen die Abfragen hier.',
    noEntries: 'Noch keine Einträge vorhanden.',
    noEntriesFiltered: 'Für den gewählten Zeitraum gibt es keine Einträge.',
    noEntriesFilteredHint: 'Bitte passen Sie den Zeitraum an oder setzen Sie den Filter zurück.',
    entriesCount: '{total} Abfragen',
    entriesCountOne: '1 Abfrage',
    entryLabel: 'Abfrage vom {time}',
    phaseValue: 'Phase {phase}',
    phaseTitle: 'Erreichte Phase der Abfrage (1–3)',
    tableCaption: 'Abfrageprotokoll: je Abfrage eine Zeile pro Aspekt.',
    paginationReport: '{first}–{last} von {total} Abfragen',
    selbesthema: 'Selbes Thema',
  },

  print: {
    logTitle: 'Abfrageprotokoll – Patient {name}',
    printNow: 'Drucken',
    /** Gemeinsame Beschriftung der Bildschirm-Werkzeugleiste (`.no-print`). */
    toolbarLabel: 'Druckaktionen',
    log: {
      heading: 'Abfrageprotokoll',
      patient: 'Patient: {name}',
      rangeBoth: 'Zeitraum: {from} – {to}',
      rangeFrom: 'Zeitraum: ab {from}',
      rangeTo: 'Zeitraum: bis {to}',
      rangeAll: 'Alle Abfragen',
      printedAt: 'Gedruckt am {date}',
      count: '{count} Abfragen',
      countOne: '1 Abfrage',
      countNone: 'Keine Abfragen',
      empty: 'Für den gewählten Zeitraum gibt es keine Abfragen zum Drucken.',
      emptyHint: 'Bitte kehren Sie zurück und passen Sie den Zeitraum an.',
      /** Reißleine in `fetchAllEntries` hat gegriffen – der Ausdruck ist unvollständig. */
      truncated: 'Achtung: Es werden nur die ersten {count} Einträge gedruckt.',
      loadFailed: 'Die Abfragen konnten nicht geladen werden.',
      tableCaption: 'Abfrageprotokoll für den Ausdruck: je Abfrage eine Zeile pro Aspekt.',
    },
    infoSheet: {
      title: 'Informationsblatt für den Patienten',
      printButton: 'Informationsblatt drucken',
      patientLabel: 'Patienten-Nummer:',
      downloadIntroBefore: 'Bitte downloaden Sie die ',
      downloadIntroStrong: 'SELWA-App',
      downloadIntroAfter: ' im Google Play Store bzw. im Apple Appstore.',
      qrAlternative: 'Alternativ können Sie auch den passenden QR-Code einscannen:',
      pairingIntro:
        'Beim Start der App werden Sie aufgefordert, den folgenden QR-Code einzuscannen:',
      playStoreAlt: 'Jetzt bei Google Play',
      playStoreQrAlt: 'QR-Code zum Google Play Store',
      appStoreAlt: 'Laden im App Store',
      appStoreQrAlt: 'QR-Code zum Apple App Store',
      /** Neu gegenüber der Alt-Anwendung: macht Ausdrucke unterscheidbar. */
      patientLine: 'Patienten-ID: {name}',
      pairingQrAlt: 'Persönlicher Kopplungs-QR-Code für Patient {name}',
      loadFailed: 'Die Patientendaten konnten nicht geladen werden.',
    },
  },

  license: {
    title: 'Lizenz',
    label: 'Lizenz:',
    patientsUsed: '{used}/{max} Patienten angelegt',
    validUntil: 'Gültig bis:',
    none: 'Es ist keine Lizenz hinterlegt.',
    warningQuota:
      'Sie haben {used} von {max} Patientenplätzen belegt. Bitte kontaktieren Sie Susanne Thielen, wenn Sie mehr Plätze benötigen.',
    warningExpiry:
      'Ihre Lizenz läuft in {days} Tagen ab. Bitte kontaktieren Sie Susanne Thielen zur Verlängerung.',
    warningExpiryOne:
      'Ihre Lizenz läuft morgen ab. Bitte kontaktieren Sie Susanne Thielen zur Verlängerung.',
    expired: 'Ihre Lizenz ist abgelaufen. Bitte kontaktieren Sie Susanne Thielen.',
    quotaFull: 'Alle Patientenplätze Ihrer Lizenz sind belegt.',
    /** Kartenüberschrift: "Lizenz: Jahreslizenz". */
    heading: 'Lizenz: {description}',
    progressLabel: 'Belegte Patientenplätze',
    /** Barrierefreie Beschriftung des Fortschrittsbalkens. */
    progressAria: '{used} von {max} Patientenplätzen belegt',
    remaining: 'Noch {count} Plätze frei',
    remainingOne: 'Noch 1 Platz frei',
    /** Kurzmarkierung hinter dem Ablaufdatum. */
    expiredBadge: '(abgelaufen)',
    warningExpiryToday:
      'Ihre Lizenz läuft heute ab. Bitte kontaktieren Sie Susanne Thielen zur Verlängerung.',
    noneHint: 'Bitte kontaktieren Sie Susanne Thielen, um eine Lizenz zu erhalten.',
    loadFailed: 'Die Lizenzdaten konnten nicht geladen werden.',
  },

  imprint: {
    title: 'Impressum',
    operatorLabel: 'Betreiber:',
    operatorName: 'Susanne Thielen',
    operatorStreet: 'Dammer Str. 112',
    operatorCity: 'D-41066 Mönchengladbach',
  },

  admin: {
    title: 'Verwaltung',
    therapists: {
      title: 'Therapeuten',
      username: 'Benutzername',
      role: 'Rolle',
      roleAdmin: 'Administrator',
      roleTherapist: 'Therapeut',
      active: 'Aktiv',
      license: 'Lizenz',
      patients: 'Patienten',
      patientsUsed: '{used}/{max}',
      create: 'Therapeut anlegen',
      createTitle: 'Neuen Therapeuten anlegen',
      resetPassword: 'Passwort zurücksetzen',
      resetPasswordTitle: 'Passwort zurücksetzen',
      newPassword: 'Neues Passwort',
      deactivate: 'Deaktivieren',
      activate: 'Aktivieren',
      makeAdmin: 'Zum Administrator machen',
      revokeAdmin: 'Administratorrechte entziehen',
      created: 'Der Therapeut wurde angelegt.',
      updated: 'Die Änderungen wurden gespeichert.',
      passwordReset: 'Das Passwort wurde zurückgesetzt.',
      empty: 'Es sind keine Therapeuten angelegt.',

      /* --- F4: Tabelle, Dialoge und Bestätigungen --------------------- */
      createAction: 'Neuer Therapeut',
      tableCaption: 'Alle Therapeutenkonten mit Status, Lizenz und Patientenzahl.',
      status: 'Status',
      statusActive: 'Aktiv',
      statusInactive: 'Deaktiviert',
      tagAdmin: 'Administrator',
      tagPasswordPending: 'Passwortänderung ausstehend',
      licenseNone: 'Keine Lizenz',
      licenseValidUntil: 'Gültig bis {date}',
      actions: 'Aktionen',
      actionsFor: 'Aktionen für {username}',
      usernameHint: 'Erlaubt sind Buchstaben, Ziffern sowie . _ - + @ – keine Leerzeichen.',
      usernameRequired: 'Bitte geben Sie einen Benutzernamen ein.',
      passwordRequired: 'Bitte vergeben Sie ein Passwort.',
      passwordTooShort: 'Das Passwort muss mindestens 12 Zeichen lang sein.',
      initialPassword: 'Erstpasswort',
      generate: 'Passwort erzeugen',
      copy: 'Kopieren',
      copied: 'Das Passwort wurde in die Zwischenablage kopiert.',
      copyFailed: 'Das Passwort konnte nicht kopiert werden. Bitte notieren Sie es manuell.',
      showOnceTitle: 'Dieses Passwort wird nur einmal angezeigt',
      showOnceHint:
        'Notieren Sie es jetzt und geben Sie es persönlich weiter. Nach dem Schließen '
        + 'des Fensters lässt es sich nicht erneut anzeigen.',
      mustChangeHint: 'Der Therapeut muss das Passwort bei der nächsten Anmeldung ändern.',
      resetPasswordFor: 'Passwort für {username} zurücksetzen',
      resetPasswordWarning: 'Alle offenen Sitzungen dieses Therapeuten werden beendet.',
      deactivateTitle: 'Therapeut deaktivieren',
      deactivateQuestion: 'Möchten Sie {username} wirklich deaktivieren?',
      deactivateWarning:
        'Alle offenen Sitzungen werden sofort beendet; eine Anmeldung ist danach nicht '
        + 'mehr möglich.',
      activateTitle: 'Therapeut aktivieren',
      activateQuestion: 'Möchten Sie {username} wieder aktivieren?',
      makeAdminTitle: 'Administratorrechte vergeben',
      makeAdminQuestion: 'Möchten Sie {username} Administratorrechte geben?',
      makeAdminWarning: 'Administratoren können alle Therapeuten und Lizenzen verwalten.',
      revokeAdminTitle: 'Administratorrechte entziehen',
      revokeAdminQuestion: 'Möchten Sie {username} die Administratorrechte entziehen?',
      selfForbidden:
        'Sie können Ihr eigenes Konto weder deaktivieren noch Ihre eigenen '
        + 'Administratorrechte ändern.',
      selfHint: 'Das eigene Konto lässt sich hier nicht ändern.',
      activated: 'Der Therapeut wurde aktiviert.',
      deactivated: 'Der Therapeut wurde deaktiviert.',
      adminGranted: 'Die Administratorrechte wurden vergeben.',
      adminRevoked: 'Die Administratorrechte wurden entzogen.',
      createFailed: 'Der Therapeut konnte nicht angelegt werden.',
      updateFailed: 'Die Änderung konnte nicht gespeichert werden.',
      passwordResetFailed: 'Das Passwort konnte nicht zurückgesetzt werden.',
      loadFailed: 'Die Therapeutenliste konnte nicht geladen werden.',
      done: 'Fertig',
    },
    licenses: {
      title: 'Lizenzen',
      therapist: 'Therapeut',
      licenseType: 'Lizenztyp',
      start: 'Beginn',
      validUntil: 'Gültig bis',
      maxPatients: 'Max. Patienten',
      durationDays: 'Laufzeit (Tage)',
      assign: 'Lizenz zuweisen',
      assignTitle: 'Lizenz zuweisen',
      assigned: 'Die Lizenz wurde zugewiesen.',
      typesTitle: 'Lizenztypen',
      description: 'Bezeichnung',
      empty: 'Es sind keine Lizenzen zugewiesen.',

      /* --- F4: Zuweisungstabelle und Dialog --------------------------- */
      assignmentsTitle: 'Zugewiesene Lizenzen',
      assignmentsCaption: 'Alle Lizenzzuweisungen mit Laufzeit und Status.',
      typesCaption: 'Verfügbare Lizenztypen mit Kontingent und Laufzeit.',
      status: 'Status',
      statusActive: 'Aktiv',
      statusExpired: 'Abgelaufen',
      typesEmpty: 'Es sind keine Lizenztypen hinterlegt.',
      therapistPlaceholder: 'Therapeut wählen',
      licenseTypePlaceholder: 'Lizenztyp wählen',
      therapistRequired: 'Bitte wählen Sie einen Therapeuten.',
      licenseTypeRequired: 'Bitte wählen Sie einen Lizenztyp.',
      startRequired: 'Bitte wählen Sie ein Startdatum.',
      typeOption: '{description} – {max} Patienten, {days} Tage',
      assignFailed: 'Die Lizenz konnte nicht zugewiesen werden.',
      loadFailed: 'Die Lizenzdaten konnten nicht geladen werden.',
      unknownTherapist: 'Unbekannter Therapeut',
      unknownType: 'Unbekannter Lizenztyp',
    },
    forbidden: 'Dieser Bereich ist Administratoren vorbehalten.',
  },

  common: {
    loading: 'Laden…',
    loadingLong: 'Die Daten werden geladen …',
    saving: 'Wird gespeichert …',
    empty: 'Keine Einträge vorhanden.',
    emptyTitle: 'Nichts vorhanden',
    error: 'Es ist ein Fehler aufgetreten.',
    errorTitle: 'Fehler',
    networkError: 'Der Server ist nicht erreichbar. Bitte prüfen Sie Ihre Internetverbindung.',
    notFound: 'Seite nicht gefunden.',
    notFoundTitle: 'Seite nicht gefunden',
    notFoundHint: 'Die aufgerufene Adresse existiert nicht.',
    comingSoon: 'Dieser Bereich kommt noch.',
    yes: 'Ja',
    no: 'Nein',
    of: 'von',
    page: 'Seite',
    unknown: 'Unbekannt',
    dash: '—',
  },

  /**
   * Server-Fehlercodes → deutsche Meldungen. Der Server liefert
   * `{"error":{"code":…}}`; die Suche erfolgt case-insensitiv
   * (`invalid_credentials` == `INVALID_CREDENTIALS`).
   */
  errorCodes: {
    INVALID_CREDENTIALS: 'Benutzername oder Passwort falsch.',
    LICENSE_EXPIRED:
      'Keine Lizenz vorhanden oder Lizenz abgelaufen. Bitte kontaktieren Sie Susanne Thielen.',
    QUOTA_EXCEEDED: 'Sie haben die maximale Anzahl an Benutzern für ihre Lizenz erreicht.',
    PATIENT_EXISTS: 'Patient existiert schon.',
    VALIDATION_ERROR: 'Die Eingaben sind unvollständig oder ungültig.',
    RATE_LIMITED: 'Zu viele Anfragen. Bitte warten Sie einen Moment.',
    FORBIDDEN: 'Sie haben keine Berechtigung für diese Aktion.',
    NOT_FOUND: 'Der angeforderte Eintrag wurde nicht gefunden.',
    UNAUTHORIZED: 'Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.',
    CONFLICT: 'Der Eintrag existiert bereits.',
    BAD_REQUEST: 'Die Anfrage konnte nicht verarbeitet werden.',
    METHOD_NOT_ALLOWED: 'Die Anfrage konnte nicht verarbeitet werden.',
    INTERNAL_ERROR: 'Auf dem Server ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
    NETWORK_ERROR: 'Der Server ist nicht erreichbar. Bitte prüfen Sie Ihre Internetverbindung.',
    UNKNOWN: 'Es ist ein unerwarteter Fehler aufgetreten.',
  },
} as const

export type Strings = typeof de
export type ErrorCode = keyof typeof de.errorCodes

/** Ersetzt `{platzhalter}` durch die übergebenen Werte. */
export function format(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in values ? String(values[key]) : match,
  )
}

/** Übersetzt einen Server-Fehlercode (beliebige Schreibweise) ins Deutsche. */
export function messageForCode(code: string | null | undefined): string {
  if (!code) return de.errorCodes.UNKNOWN
  const key = code.toUpperCase().replace(/[-\s]/g, '_') as ErrorCode
  return de.errorCodes[key] ?? de.errorCodes.UNKNOWN
}

export default de
